import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../App";
import { 
  LayoutDashboard, 
  FileText, 
  Calendar,
  Users, 
  ClipboardList,
  LogOut,
  Truck,
  Menu,
  X,
  Settings,
  ClipboardCheck
} from "lucide-react";
import { useState } from "react";

const AdminLayout = ({ children, title }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: "Dashboard", href: "/admin", icon: LayoutDashboard },
    { name: "Quotes", href: "/admin/quotes", icon: FileText },
    { name: "Bookings", href: "/admin/bookings", icon: ClipboardList },
    { name: "Surveys", href: "/admin/surveys", icon: ClipboardCheck },
    { name: "Customers", href: "/admin/customers", icon: Users },
    { name: "Calendar", href: "/admin/calendar", icon: Calendar },
    { name: "Pricing Rules", href: "/admin/pricing-rules", icon: Settings },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-[#0F172A] transform transition-transform duration-200 lg:translate-x-0 ${
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      }`}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-700">
          <Link to="/admin" className="flex items-center gap-2">
            <Truck className="w-6 h-6 text-[#FF5A1F]" />
            <span className="font-['Outfit'] font-bold text-white">HITECH ADMIN</span>
          </Link>
          <button 
            className="lg:hidden text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive 
                    ? "bg-[#FF5A1F] text-white" 
                    : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-700">
          <div className="text-sm text-slate-400 mb-3">
            {user?.email}
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 text-slate-300 hover:text-white text-sm"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-['Outfit'] font-bold text-[#0F172A]">{title}</h1>
          </div>
          <a 
            href="/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-slate-500 hover:text-[#FF5A1F]"
          >
            View Website →
          </a>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
